// Durable client-side cache for the separation engine files.
//
// The engine weights are ~150 MB fetched from /vendor on first separation.
// The browser's HTTP cache usually keeps them, but entries that big are
// eviction bait — so we ALSO store the bytes in the Cache Storage API under
// our own cache and ask for persistent storage (the pattern transformers.js /
// WebLLM use). Result: one download ever per browser, surviving restarts.
//
// A guarded idle prefetch warms this cache when the Studio is visited, so
// most users have the engine before they ever hit "isolate".

const CACHE_NAME = 'rl-engine-v1';

function cachesAvailable(): boolean {
  return typeof caches !== 'undefined';
}

// ── Is it really a model? ───────────────────────────────────────────────────
// A model URL that is missing on the server does not 404. The SPA fallback answers
// it with index.html: `200 text/html` in production (nginx→Apache) and in Vite dev,
// and `206 text/html` to a Range request. Stored as-is, those ~5 KB of HTML became
// "the model" for good — ORT rejects them with "protobuf parsing failed" on every
// later run, even once the file is back. Every model we ship is tens of MB (the
// smallest, the phone engine, is 33 MB), so anything under this floor is not one.
const MIN_MODEL_BYTES = 1024 * 1024;

/** The SPA's index.html fallback, not a file. */
function isPage(res: Response): boolean {
  return /^text\/html/i.test(res.headers.get('content-type') || '');
}

/**
 * The body of a model download, or an error when the server answered with
 * something that can't be one. Call it BEFORE the bytes are cached or used.
 */
export async function readModelBody(res: Response, url: string): Promise<Uint8Array> {
  if (isPage(res)) throw new Error(`model fetch failed: ${url} is missing on the server (got a web page)`);
  const bytes = new Uint8Array(await res.arrayBuffer());
  if (bytes.byteLength < MIN_MODEL_BYTES) throw new Error(`model fetch failed: ${url} is only ${bytes.byteLength} bytes`);
  return bytes;
}

/** ORT's verdict on bytes that are not an ONNX model at all. */
export function isModelParseError(message: string): boolean {
  return /protobuf parsing failed/i.test(message);
}

/**
 * Forget everything stored for `url`: the model entry and any half-finished
 * download of it. For bytes ORT refused to parse — left in the cache they fail
 * the same way on every later run, so the next attempt has to download afresh.
 */
export async function dropEngineBytes(url: string, cacheName: string = CACHE_NAME): Promise<void> {
  if (!cachesAvailable()) return;
  try {
    const c = await caches.open(cacheName);
    const abs = new URL(url, location.href).href;
    const mine = (await c.keys()).filter((r) => r.url === abs || r.url.startsWith(abs + '?rl'));
    await Promise.all(mine.map((r) => c.delete(r)));
  } catch {
    /* best-effort */
  }
}

/** Cached engine bytes for `url`, or null (cache miss / API unavailable). */
export async function cachedEngineBytes(url: string, cacheName: string = CACHE_NAME): Promise<Uint8Array | null> {
  if (!cachesAvailable()) return null;
  try {
    const c = await caches.open(cacheName);
    const hit = await c.match(url);
    if (!hit) return null;
    const bytes = new Uint8Array(await hit.arrayBuffer());
    // Stored before the size guard existed, this can be the SPA's index.html:
    // drop it, so this is a miss and the caller downloads the real file.
    if (bytes.byteLength < MIN_MODEL_BYTES) {
      await c.delete(url);
      return null;
    }
    return bytes;
  } catch {
    return null;
  }
}

/**
 * Store engine bytes under `url`. Awaited by callers BEFORE transferring the
 * buffer to a worker (cache.put reads the body during the call). Failures are
 * swallowed — worst case the next visit downloads again.
 */
export async function putEngineBytes(url: string, bytes: Uint8Array, cacheName: string = CACHE_NAME): Promise<void> {
  if (!cachesAvailable()) return;
  if (bytes.byteLength < MIN_MODEL_BYTES) return; // never store what can't be a model
  try {
    const c = await caches.open(cacheName);
    await c.put(
      url,
      new Response(bytes as BufferSource, {
        headers: {
          'Content-Type': 'application/octet-stream',
          'Content-Length': String(bytes.byteLength),
        },
      }),
    );
  } catch {
    /* quota/full/incognito — fine */
  }
}

/**
 * Is this engine already stored locally? A presence check only — it never reads
 * the body, which for the "Best" engine is 336 MB. Used to decide whether to warn
 * about a first-use setup wait, so repeat users are not nagged about a download
 * that will not happen.
 */
export async function engineCached(url: string, cacheName: string = CACHE_NAME): Promise<boolean> {
  if (!cachesAvailable()) return false;
  try {
    const c = await caches.open(cacheName);
    const hit = await c.match(url);
    if (!hit) return false;
    // A stored page is not an engine; its length says so without reading it.
    const len = Number(hit.headers.get('content-length'));
    return !(len > 0 && len < MIN_MODEL_BYTES);
  } catch { return false; }
}

// ── Resumable, chunked engine download ──────────────────────────────────────
// The weights are one ~142 MB file. Streaming it in a single fetch meant that an
// interrupted download — a closed tab, a dropped connection, a navigation — threw
// away EVERY byte, because the cache write only happened after the last one. One
// user pulled 25 MB, then 28 MB, then 39 MB of a 64 MB model across three visits
// and still had nothing. So: fetch it in 8 MB ranges and persist each range the
// moment it lands. A later visit resumes at the first missing range.
//
// Keys live under `?rlpart=<i>` / `?rlmeta=1` on the model URL. It must be a
// QUERY, not a fragment: Cache Storage keys by Request URL and Request strips
// fragments, so `#part=0` would collapse onto the real model entry and corrupt it.
const PART_BYTES = 8 * 1024 * 1024;
const partUrl = (url: string, i: number) => `${url}?rlpart=${i}`;
const metaUrl = (url: string) => `${url}?rlmeta=1`;

/** Progress for a byte-wise download: fraction plus the raw counts for a label. */
export type BytesProgress = (frac: number, received: number, total: number) => void;

interface PartMeta { total: number; tag: string }

async function readMeta(c: Cache, url: string): Promise<PartMeta | null> {
  try {
    const r = await c.match(metaUrl(url));
    return r ? ((await r.json()) as PartMeta) : null;
  } catch { return null; }
}

async function dropParts(c: Cache, url: string, nParts: number): Promise<void> {
  const jobs: Promise<unknown>[] = [c.delete(metaUrl(url)).catch(() => false)];
  for (let i = 0; i < nParts; i++) jobs.push(c.delete(partUrl(url, i)).catch(() => false));
  await Promise.all(jobs);
}

/**
 * One-byte range probe: returns the file's total size and a validator, or null
 * when the server won't do ranges (then the caller streams it the old way).
 */
async function probeRange(url: string): Promise<PartMeta | null> {
  const res = await fetch(url, { headers: { Range: 'bytes=0-0' } });
  if (res.status !== 206) return null;
  const total = Number((res.headers.get('content-range') || '').split('/')[1] || 0);
  await res.arrayBuffer().catch(() => undefined); // drain the 1 byte
  // A missing model is the SPA's index.html, which the server ranges too. Never
  // fetch that in parts: the plain stream below refuses it outright.
  if (!total || total < MIN_MODEL_BYTES || isPage(res)) return null;
  return { total, tag: res.headers.get('etag') || res.headers.get('last-modified') || String(total) };
}

/** Plain single-stream download — the original path, and the fallback. */
async function streamDownload(url: string, onProgress?: BytesProgress): Promise<Uint8Array> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Engine fetch failed: HTTP ${res.status}`);
  if (isPage(res)) throw new Error(`Engine fetch failed: ${url} is missing on the server (got a web page)`);
  const total = Number(res.headers.get('content-length')) || 0;
  const reader = res.body!.getReader();
  const chunks: Uint8Array[] = [];
  let received = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    received += value.length;
    if (total) onProgress?.(received / total, received, total);
  }
  if (received < MIN_MODEL_BYTES) throw new Error(`Engine fetch failed: ${url} is only ${received} bytes`);
  const out = new Uint8Array(received);
  let off = 0;
  for (const c of chunks) { out.set(c, off); off += c.length; }
  return out;
}

/**
 * Engine bytes for `url`: durable cache → resumable ranged download → assemble.
 * Never throws for cache reasons; any failure of the resumable path falls back
 * to a plain stream so this can only ever be as good as the old behaviour.
 */
export async function fetchEngineBytes(url: string, onProgress?: BytesProgress): Promise<Uint8Array> {
  const done = await cachedEngineBytes(url);
  if (done) return done;
  if (!cachesAvailable()) return streamDownload(url, onProgress);

  try {
    const c = await caches.open(CACHE_NAME);
    const meta = await probeRange(url);
    if (!meta) return streamDownload(url, onProgress); // no range support

    // A changed file invalidates whatever we had half-downloaded.
    const prev = await readMeta(c, url);
    if (prev && (prev.tag !== meta.tag || prev.total !== meta.total)) {
      await dropParts(c, url, Math.ceil(prev.total / PART_BYTES));
    }
    const nParts = Math.ceil(meta.total / PART_BYTES);
    try {
      await c.put(metaUrl(url), new Response(JSON.stringify(meta), { headers: { 'Content-Type': 'application/json' } }));
    } catch { /* quota — parts still work, we just re-probe next time */ }

    // Fill the gaps. Each part is persisted as it arrives and NOT retained, so
    // an interruption keeps everything already on disk and costs one part at most.
    let received = 0;
    for (let i = 0; i < nParts; i++) {
      const key = partUrl(url, i);
      const start = i * PART_BYTES;
      const end = Math.min(start + PART_BYTES, meta.total) - 1;
      const cached = await c.match(key);
      if (cached) {
        received += Number(cached.headers.get('content-length')) || (end - start + 1);
        onProgress?.(Math.min(1, received / meta.total), received, meta.total);
        continue;
      }
      const res = await fetch(url, { headers: { Range: `bytes=${start}-${end}` } });
      if (res.status !== 206 || isPage(res)) throw new Error('range request refused mid-download');
      const buf = await res.arrayBuffer();
      // Exactly the range asked for, or every byte after it assembles misaligned.
      if (buf.byteLength !== end - start + 1) throw new Error('range answered with the wrong length');
      await c.put(key, new Response(buf, { headers: { 'Content-Length': String(buf.byteLength) } }));
      received += buf.byteLength;
      onProgress?.(Math.min(1, received / meta.total), received, meta.total);
    }

    // Every part is on disk: assemble once, promote to the canonical entry so
    // future loads are a single read, then reclaim the part storage.
    const out = new Uint8Array(meta.total);
    let off = 0;
    for (let i = 0; i < nParts; i++) {
      const r = await c.match(partUrl(url, i));
      if (!r) throw new Error('cached part disappeared');
      const b = new Uint8Array(await r.arrayBuffer());
      out.set(b, off);
      off += b.byteLength;
    }
    await putEngineBytes(url, out);
    await dropParts(c, url, nParts);
    return out;
  } catch {
    return streamDownload(url, onProgress);
  }
}

/** True on connections where a ~150 MB background download would be rude. */
function constrainedConnection(): boolean {
  const conn = (navigator as unknown as { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
  if (!conn) return false;
  if (conn.saveData) return true;
  return /(^|-)2g|3g/.test(conn.effectiveType || '');
}

let prefetchScheduled = false;

/**
 * Idle-time warm of the engine cache: `cacheUrls` go into Cache Storage
 * (durable), `httpWarmUrls` are plain fetches that just warm the HTTP disk
 * cache (for files the worker loads itself, e.g. the runtime wasm).
 *
 * Polite by design: waits for load + idle, skips Data-Saver and slow
 * connections, runs once per page.
 */
export function scheduleEnginePrefetch(cacheUrls: string[], httpWarmUrls: string[] = []): void {
  if (prefetchScheduled || !cachesAvailable()) return;
  prefetchScheduled = true;

  const run = async () => {
    if (constrainedConnection() || !navigator.onLine) return;
    try {
      // Persistent storage exempts our cache from eviction. Chrome grants
      // this silently based on engagement — no prompt, safe to always ask.
      navigator.storage?.persist?.().catch(() => {});
      const c = await caches.open(CACHE_NAME);
      // Drop entries for files we no longer ship (renames/upgrades). Partial
      // downloads (`?rlpart=`/`?rlmeta=`) belong to a kept URL and must survive,
      // or the prefetch would wipe the very progress it is meant to continue.
      const keep = new Set(cacheUrls.map((u) => new URL(u, location.origin).href));
      const belongsToKept = (u: string) => [...keep].some((k) => u === k || u.startsWith(k + '?rl'));
      for (const req of await c.keys()) {
        if (!belongsToKept(req.url)) c.delete(req).catch(() => {});
      }
      // Resumable, so an idle warm that gets interrupted still banks its progress
      // for the next visit (c.add() would have discarded it).
      // engineCached, not a bare match: a stored page must be replaced, not kept.
      for (const url of cacheUrls) {
        if (!(await engineCached(url))) await fetchEngineBytes(url);
      }
      for (const url of httpWarmUrls) {
        fetch(url, { cache: 'force-cache' }).catch(() => {});
      }
    } catch {
      /* best-effort */
    }
  };

  // Give the page a moment to settle, then wait for an idle slot. Kept short:
  // this download is the long pole on a first visit (~142MB), and every second
  // spent waiting here is a second the user may spend clicking Isolate instead —
  // at which point they watch the same download in the foreground.
  window.setTimeout(() => {
    const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void })
      .requestIdleCallback;
    if (ric) ric(() => void run(), { timeout: 4_000 });
    else void run();
  }, 1500);
}
